/**
 * The shipment email (user, 2026-10-26): the location's Shipment template
 * (Settings -> Locations) filled from a Shipments row. There is no built-in
 * email: a location with no template shows an empty Subject and Body (user:
 * "if there is no template then show nothing").
 *
 * Lives in its own module, with no imports, so it can be tested without
 * pulling in services/axios (ESM, not transformed by CRA's Jest).
 */

const escapeHtml = (v) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/**
 * "JOHN O'BRIEN-SMITH" -> "John O'Brien-Smith" (user, 2026-10-29: WorldShip
 * exports the contact name in capitals). A capital for the first letter
 * after anything that is not a letter: each word, and after a hyphen or an
 * apostrophe ("O'Brien-Smith").
 */
export const titleCase = (v) =>
  String(v ?? "")
    .toLowerCase()
    .replace(/(^|[^\p{L}])(\p{L})/gu, (m, sep, ch) => sep + ch.toUpperCase());

const filled = (v) => v !== null && v !== undefined && String(v).trim() !== "";

/**
 * The location's Shipment template (Settings -> Locations -> email templates,
 * type "shipment"; user, 2026-10-26: "Email template location wise with
 * variables related to shipment"), filled from the row. The variables are
 * the buttons that page offers (LOCATION_EMAIL_TEMPLATE_MODULES), written
 * << Label >>; the editor may have stored the brackets as &lt;&lt; &gt;&gt;.
 *
 * In the body every value is HTML-escaped and << Tracking Link >> becomes a
 * link showing the full URL (user, 2026-10-27: "show full link not link
 * text"); in the subject (plain text) it is the bare URL.
 *
 * Grouped in the order the user gave (2026-10-27): the customer, then the
 * tracking link and number, then the ship-to company and contact, then the
 * rest.
 */
export const SHIPMENT_TEMPLATE_VARIABLES = {
  "CUSTOMER ID": " << Customer ID >> ",
  "CUSTOMER NAME": " << Customer Name >> ",
  "TRACKING LINK": " << Tracking Link >> ",
  "TRACKING #": " << Tracking Number >> ",
  "COMPANY NAME": " << Company Name >> ",
  "CONTACT NAME": " << Contact Name >> ",
  "SHIP TO ADDRESS": " << Ship To Address >> ",
  COURIER: " << Courier >> ",
  SERVICE: " << Service >> ",
  "SHIP DATE": " << Ship Date >> ",
  PACKAGES: " << Packages >> ",
  "W-ID": " << ID >> ",
  "LOCATION NAME": " << Location Name >> ",
};

/**
 * The Multiple Shipments template (Bulk Email, user 2026-10-27): one email
 * for several shipments. << Shipments >> is every selected shipment's slip
 * -- the clipboard button's text: tracking link, then the shipment info --
 * one after another. The rest are the first shipment's customer and
 * location.
 */
export const MULTIPLE_SHIPMENT_TEMPLATE_VARIABLES = {
  "CUSTOMER ID": " << Customer ID >> ",
  "CUSTOMER NAME": " << Customer Name >> ",
  SHIPMENTS: " << Shipments >> ",
  "COMPANY NAME": " << Company Name >> ",
  "CONTACT NAME": " << Contact Name >> ",
  "LOCATION NAME": " << Location Name >> ",
};

const linkHtml = (url) =>
  `<a href="${escapeHtml(url)}">${escapeHtml(url)}</a>`;

const escapeRegExp = (value) => String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const replaceToken = (text, label, replacement) => {
  const pattern = new RegExp(
    `(?:&lt;&lt;|<<)\\s*${escapeRegExp(label).replace(/\s+/g, "\\s+")}\\s*(?:&gt;&gt;|>>)`,
    "gi",
  );
  return String(text || "").replace(pattern, () => replacement ?? "");
};

/**
 * text   the template's subject or body
 * item   a Shipments list row
 * trackingUrl  its courier tracking link; shipDate  as the page shows it;
 * locationName the sending location's name
 * html   true for the body (escape values, real link), false for the subject
 */
export const fillShipmentTemplate = (
  text,
  item,
  { trackingUrl, shipDate, locationName } = {},
  html = true,
) => {
  const esc = html ? escapeHtml : (v) => String(v ?? "");
  const shipTo = [
    [item?.address1, item?.address2].filter(filled).join(" "),
    [item?.city, item?.state, item?.zip].filter(filled).join(" "),
  ].filter(filled);
  const trackingLink = trackingUrl
    ? html
      ? linkHtml(trackingUrl)
      : trackingUrl
    : esc(item?.trackingNumber);

  const values = {
    "Tracking Number": esc(item?.trackingNumber),
    "Tracking Link": trackingLink,
    Courier: esc(item?.courier?.name),
    Service: esc(item?.service),
    "Ship Date": esc(shipDate),
    Packages: esc(item?.packages),
    ID: esc(item?.woId ? `W-${item.woId}` : ""),
    "Customer ID": esc(item?.customer?.customerId ? `C-${item.customer.customerId}` : ""),
    "Customer Name": esc(item?.customer?.customerName),
    "Company Name": esc(item?.customerName),
    "Contact Name": esc(titleCase(item?.contactName)),
    "Ship To Address": html ? shipTo.map(escapeHtml).join("<br>") : shipTo.join(", "),
    "Location Name": esc(locationName ?? item?.location?.locationName),
  };
  return Object.entries(values).reduce(
    (out, [label, value]) => replaceToken(out, label, value),
    String(text || ""),
  );
};

/**
 * A shipment slip (the clipboard text: link, blank line, info) as email HTML:
 * the link clickable, each line on its own line.
 */
export const slipToHtml = (slip) =>
  String(slip || "")
    .split("\n")
    .map((line) =>
      /^https?:\/\/\S+$/.test(line.trim()) ? linkHtml(line.trim()) : escapeHtml(line),
    )
    .join("<br>");

/**
 * text   the Multiple Shipments template's subject or body
 * items  the selected rows; slips  their clipboard texts, same order
 * html   true for the body; false for the subject
 *
 * A body with no << Shipments >> gets the list on the end, so the shipments
 * are never left out; no template at all is just the list.
 */
export const fillMultipleShipmentTemplate = (
  text,
  items = [],
  { slips = [], locationName } = {},
  html = true,
) => {
  const first = items[0] || {};
  const esc = html ? escapeHtml : (v) => String(v ?? "");
  const list = html
    ? slips.map(slipToHtml).join("<br><br>")
    : items.map((i) => i?.trackingNumber).filter(filled).join(", ");
  const values = {
    "Customer ID": esc(first?.customer?.customerId ? `C-${first.customer.customerId}` : ""),
    "Customer Name": esc(first?.customer?.customerName),
    "Company Name": esc(first?.customerName),
    "Contact Name": esc(titleCase(first?.contactName)),
    "Location Name": esc(locationName ?? first?.location?.locationName),
    Shipments: list,
  };
  const source = String(text || "");
  const hasList = /(?:&lt;&lt;|<<)\s*Shipments\s*(?:&gt;&gt;|>>)/i.test(source);
  const out = Object.entries(values).reduce(
    (acc, [label, value]) => replaceToken(acc, label, value),
    source,
  );
  if (!html || hasList) return out;
  return out.trim() ? `${out}<br>${list}` : list;
};

/**
 * Quill writes every line as a <p>; Quill shows them with no gap, but mail
 * clients give each <p> a margin, so a sent email read double spaced (user,
 * 2026-10-27). Zero margin on each paragraph sends what the editor showed.
 */
export const tightenParagraphs = (html) =>
  String(html || "").replace(/<p(\s[^>]*)?>/gi, (tag, attrs = "") =>
    /style\s*=/i.test(attrs)
      ? tag.replace(/style\s*=\s*"/i, 'style="margin:0;')
      : `<p${attrs} style="margin:0">`,
  );

export default fillShipmentTemplate;
